import { useCallback, useRef } from "react";

/**
 * 处理输入法编辑状态的 Hook
 * 用于判断用户是否正在使用输入法（如中文拼音输入）
 * 
 * @returns {Object} 包含输入法状态和事件处理器
 * @property {React.RefObject<boolean>} isComposingRef - 输入法编辑状态的 ref
 * @property {() => void} handleCompositionStart - 输入法开始编辑的处理函数
 * @property {() => void} handleCompositionEnd - 输入法结束编辑的处理函数
 * 
 * @example
 * ```tsx
 * const { isComposingRef, handleCompositionStart, handleCompositionEnd } = useComposition();
 * 
 * const handleKeyDown = (e: React.KeyboardEvent) => {
 *   if (isComposingRef.current) {
 *     return; // 输入法编辑中，忽略回车等按键
 *   }
 *   // 处理按键逻辑
 * };
 * 
 * return (
 *   <input
 *     onCompositionStart={handleCompositionStart}
 *     onCompositionEnd={handleCompositionEnd}
 *     onKeyDown={handleKeyDown}
 *   />
 * );
 * ```
 */
export const useComposition = () => {
    const isComposingRef = useRef(false);

    /**
     * 处理输入法开始编辑
     */
    const handleCompositionStart = useCallback(() => {
        isComposingRef.current = true;
    }, []);

    /**
     * 处理输入法结束编辑
     */
    const handleCompositionEnd = useCallback(() => {
        isComposingRef.current = false;
    }, []);

    return {
        isComposingRef,
        handleCompositionStart,
        handleCompositionEnd,
    };
};